import React, { useState, useEffect, CSSProperties, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import useUser from 'Hooks/useUser';
import useScreen from 'Hooks/useScreen';
import CardSymphony from 'elements/CardSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import InputSymphony from 'elements/InputSymphony';
import Icon from 'elements/Icon';
import Loader from 'elements/Loader';
import { styx, clsx } from 'modules/Utils';
import Banner from 'elements/Banner';
import CloudinaryUploadWidget, { CloudinaryUploadedFileType } from 'components/CampaignViews/CloudinaryUploadWidget';
import { AssetPlatformType } from 'components/CampaignViews/AdCreativeSelectorCampaignView';
import { toast } from 'react-toastify';
import { processVideo_api } from 'api/adminApi';

/**
 * Interface for a clipped video segment
 */
interface VideoClip {
  id: string;
  url: string;
  thumbnailUrl: string;
  startTime: number;
  endTime: number;
  duration: number;
}

/**
 * Clipper component allows admins to upload videos and generate clips at specified intervals
 */
const Clipper: React.FC = () => {
  const { isSymphonyAdmin: isAdmin } = useUser();
  const { mStyle, mobileView } = useScreen();

  // State for uploaded video
  const [uploadedVideo, setUploadedVideo] = useState<CloudinaryUploadedFileType | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [clipInterval, setClipInterval] = useState<number>(5);
  const [clipLength, setClipLength] = useState<number>(30);
  const [generatedClips, setGeneratedClips] = useState<VideoClip[]>([]);

  // Button reference for CloudinaryUploadWidget
  const buttonRef = useRef<HTMLButtonElement>(null);

  /**
   * Handles video upload from Cloudinary
   * @param info - The uploaded file information
   */
  const handleVideoUpload = (info: CloudinaryUploadedFileType) => {
    // Only accept video files
    if (info.resource_type !== 'video') {
      toast.error('Please upload a video file');
      return;
    }

    setUploadedVideo(info);
    setGeneratedClips([]);
  };

  /**
   * Processes the uploaded video to generate clips at specified intervals
   */
  const handleProcessVideo = async () => {
    if (!uploadedVideo) {
      toast.error('Please upload a video first');
      return;
    }

    if (clipInterval <= 0) {
      toast.error('Clip interval must be greater than 0');
      return;
    }

    if (clipLength <= 0) {
      toast.error('Clip length must be greater than 0');
      return;
    }

    setIsProcessing(true);
    setGeneratedClips([]);

    try {
      const response = await processVideo_api({
        videoUrl: uploadedVideo.secure_url,
        videoId: uploadedVideo.public_id,
        clipInterval,
        clipLength,
      });

      if (response && response.clips) {
        setGeneratedClips(response.clips);
        toast.success(`Generated ${response.clips.length} clips successfully`);
      }
    } catch (error) {
      console.error('Error processing video:', error);
      toast.error('Failed to process video');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handles downloading a clip
   * @param clip - The clip to download
   */
  const handleDownloadClip = (clip: VideoClip) => {
    window.open(clip.url, '_blank');
  };

  /**
   * Render video upload section
   */
  const renderUploader = () => {
    return (
      <div className="uploadSection" style={sty.uploadSection}>
        <ButtonSymphony
          id="upload_video_button"
          ref={buttonRef}
          className="uploadVideoButton"
          style={sty.uploadButton}
          variant="outlined"
        >
          {uploadedVideo ? 'Upload a Different Video' : 'Upload Video'}
        </ButtonSymphony>
        <div>
          <div className="uploadDescription" style={sty.uploadDescription}>
            Upload a video file to generate clips.
          </div>
          <div className="uploadDescription" style={sty.uploadDescription}>
            Supported formats: .mp4, .mov, .avi
          </div>
          <div className="uploadDescription" style={sty.uploadDescription}>
            250 MB max file size
          </div>
        </div>
        <CloudinaryUploadWidget
          maxFileSize={500 * 1024 * 1024}
          buttonId="upload_video_button"
          onUpload={handleVideoUpload}
        />
      </div>
    );
  };

  /**
   * Render video preview section
   */
  const renderVideoPreview = () => {
    if (!uploadedVideo) return null;

    return (
      <div className="videoPreviewSection" style={sty.videoPreviewSection}>
        <h3 style={sty.sectionTitle}>Video Preview</h3>
        <div className="videoContainer" style={sty.videoContainer}>
          <video
            controls
            className="videoPlayer"
            src={uploadedVideo.secure_url}
            style={sty.video}
            poster={uploadedVideo.thumbnail_url}
          />
          <div className="videoInfo" style={sty.videoInfo}>
            <div style={sty.videoInfoItem}>
              <span style={sty.videoInfoLabel}>Filename:</span>
              <span>{uploadedVideo.original_filename}</span>
            </div>
            <div style={sty.videoInfoItem}>
              <span style={sty.videoInfoLabel}>Format:</span>
              <span>{uploadedVideo.format}</span>
            </div>
            <div style={sty.videoInfoItem}>
              <span style={sty.videoInfoLabel}>Size:</span>
              <span>{Math.round(uploadedVideo.bytes / 1024 / 1024 * 100) / 100} MB</span>
            </div>
            <div style={sty.videoInfoItem}>
              <span style={sty.videoInfoLabel}>Dimensions:</span>
              <span>{uploadedVideo.width} x {uploadedVideo.height}px</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Render clip controls section
   */
  const renderClipControls = () => {
    if (!uploadedVideo) return null;

    return (
      <div className="clipControlsSection" style={sty.clipControlsSection}>
        <h3 style={sty.sectionTitle}>Clip Settings</h3>
        <div className="clipIntervalControl" style={sty.clipIntervalControl}>
          <InputSymphony
            type="number"
            label="Clip every (seconds):"
            value={String(clipInterval)}
            onChange={(value) => setClipInterval(Number(value))}
            min={1}
            className="clipIntervalInput"
            style={sty.clipIntervalInput}
          />
          <InputSymphony
            type="number"
            label="Clip length (seconds):"
            value={String(clipLength)}
            onChange={(value) => setClipLength(Number(value))}
            min={1}
            className="clipLengthInput"
            style={sty.clipIntervalInput}
          />
          <ButtonSymphony
            className="clipButton"
            onClick={handleProcessVideo}
            isLoading={isProcessing}
            loadingText="Processing..."
            disabled={isProcessing}
            style={sty.clipButton}
          >
            Generate Clips
          </ButtonSymphony>
        </div>
      </div>
    );
  };

  /**
   * Render generated clips section
   */
  const renderGeneratedClips = () => {
    if (!generatedClips.length) return null;

    return (
      <div className="generatedClipsSection" style={sty.generatedClipsSection}>
        <h3 style={sty.sectionTitle}>Generated Clips ({generatedClips.length})</h3>
        <div className="clipsGrid" style={styx(
          sty.clipsGrid,
          mobileView && sty.clipsGrid_mobile
        )}>
          {generatedClips.map((clip, index) => (
            <div key={clip.id} className="clipItem" style={sty.clipItem}>
              <div className="clipPreview" style={sty.clipPreview}>
                <video
                  controls
                  src={clip.url}
                  style={sty.clipVideo}
                  poster={clip.thumbnailUrl}
                />
                <div className="clipInfo" style={sty.clipInfo}>
                  <span style={sty.clipTitle}>Clip {index + 1}</span>
                  <span style={sty.clipDuration}>
                    {Math.floor(clip.startTime / 60)}:{(clip.startTime % 60).toString().padStart(2, '0')} -
                    {Math.floor(clip.endTime / 60)}:{(clip.endTime % 60).toString().padStart(2, '0')}
                  </span>
                </div>
              </div>
              <ButtonSymphony
                className="downloadClipButton"
                onClick={() => handleDownloadClip(clip)}
                variant="outlined"
                style={sty.downloadButton}
              >
                <Icon icon="download" className="downloadIcon" style={sty.downloadIcon} />
                Download
              </ButtonSymphony>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Redirect if not admin
  // if (!isAdmin) {
  //   return <Redirect to="/dashboard" />;
  // }

  return (
    <div
      className="clipperContainer"
      style={styx(
        mStyle(sty, 'clipperContainer')
      )}
    >
      <CardSymphony
        className="clipperCard"
        style={styx(
          sty.clipperCard,
          mobileView && sty.clipperCard_mobile
        )}
      >
        <div className="clipperHeader" style={sty.clipperHeader}>
          <h1 style={sty.heading}>Video Clipper</h1>
          <p style={sty.subtitle}>Upload a video and generate clips at specified intervals</p>
        </div>

        <Banner
          className="clipperInfoBanner"
          color="maestro"
          title="How to use the Clipper"
          iconLeft={
            <Icon
              icon="maestroBadgeImage"
              className="maestroBannerIcon"
              size={42}
            />
          }
        >
          1. Upload a video using the button below<br />
          2. Set the interval (in seconds) for each clip<br />
          3. Click "Generate Clips" to process the video<br />
          4. Download individual clips as needed
        </Banner>

        {renderUploader()}
        {renderVideoPreview()}
        {renderClipControls()}
        {isProcessing && (
          <div className="processingLoader" style={sty.processingLoader}>
            <Loader size={40} title="Processing video... This may take a few minutes." />
          </div>
        )}
        {renderGeneratedClips()}
      </CardSymphony>
    </div>
  );
};

const sty: Record<string, CSSProperties> = {
  clipperContainer: {
    padding: 24,
    width: '100%',
  },
  clipperContainer_mobile: {
    padding: 16,
  },
  clipperCard: {
    padding: 24,
    width: '100%',
  },
  clipperCard_mobile: {
    padding: 16,
  },
  clipperHeader: {
    marginBottom: 24,
  },
  heading: {
    fontSize: 28,
    fontWeight: 600,
    margin: 0,
  },
  subtitle: {
    marginTop: 8,
    color: '#666',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 500,
    marginBottom: 16,
  },
  uploadSection: {
    marginTop: 24,
    marginBottom: 24,
  },
  uploadButton: {
    borderStyle: 'dashed',
    backgroundColor: 'rgba(103, 58, 183, 0.05)',
    borderWidth: 1,
    borderColor: '#673AB7',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    color: '#673AB7',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    height: 72
  },
  uploadDescription: {
    fontSize: 14,
    color: '#666',
  },
  videoPreviewSection: {
    marginBottom: 24,
  },
  videoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    border: '1px solid #eee',
    borderRadius: 8,
    padding: 16,
  },
  video: {
    width: '100%',
    maxHeight: 400,
    objectFit: 'contain',
    backgroundColor: '#000',
    borderRadius: 4,
  },
  videoInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  videoInfoItem: {
    display: 'flex',
    fontSize: 14,
  },
  videoInfoLabel: {
    fontWeight: 500,
    marginRight: 8,
    minWidth: 80,
  },
  clipControlsSection: {
    marginBottom: 24,
  },
  clipIntervalControl: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 16,
  },
  clipIntervalInput: {
    width: 150,
  },
  clipButton: {
    height: 40,
  },
  processingLoader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  generatedClipsSection: {
    marginTop: 24,
  },
  clipsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
  },
  clipsGrid_mobile: {
    gridTemplateColumns: '1fr',
  },
  clipItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    border: '1px solid #eee',
    borderRadius: 8,
    padding: 16,
  },
  clipPreview: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  clipVideo: {
    width: '100%',
    borderRadius: 4,
  },
  clipInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 14,
  },
  clipTitle: {
    fontWeight: 500,
  },
  clipDuration: {
    color: '#666',
  },
  downloadButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  downloadIcon: {
    fontSize: 16,
  },
};

export default Clipper; 